import { Copy, Plus, Share2, Ticket, Trash2, UserPlus, UserRoundCheck } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { copyText } from '../lib/clipboard';
import { DuesCard } from './finance';
import { useAddHousehold, useCreatePass, useHousehold, usePasses, useRemoveHousehold, useRevokePass, useTickets, useVisits } from '../lib/queries';
import { usePollMs } from '../lib/realtime';
import { useSession } from '../lib/session';
import { Badge, Button, Card, Empty, ErrorNote, Field, Input, KIND_LABEL, PageHeader, Select, Sheet, Spinner, StatusBadge, TICKET_TONE, fmtDateTime, timeAgo } from '../ui';
import { useToast } from '../ui/toast';
import { DecisionCard, VisitRow } from './visits';

export function ResidentHome() {
  const { current, user } = useSession();
  const poll = usePollMs(30_000, 6_000);
  const visits = useVisits({ limit: 20 }, poll);
  const tickets = useTickets({ mine: true, limit: 10 });
  const items = visits.data?.items ?? [];
  const pending = items.filter((v) => v.status === 'pending');
  const recent = items.filter((v) => v.status !== 'pending').slice(0, 4);
  const openTickets = (tickets.data?.items ?? []).filter((t) => t.status !== 'closed');
  const canApprove = current.canApproveVisitors;

  return (
    <div className="space-y-5">
      <PageHeader title={`Hello${user.name ? `, ${user.name.split(' ')[0]}` : ''}`} subtitle={`Flat ${current.unitLabel} · ${current.communityName}`} />

      {pending.length > 0 && (
        <section aria-label="Visitors waiting" className="space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-wide text-amber-800">At your gate now</h2>
          {canApprove ? pending.map((v) => <DecisionCard key={v.id} v={v} />) : pending.map((v) => <VisitRow key={v.id} v={v} to={`/visit/${v.id}`} />)}
          {!canApprove && <p className="text-sm text-slate-600">You can see visitors, but only the flat's approvers can allow or reject them.</p>}
        </section>
      )}

      {(current.role === 'owner' || current.role === 'tenant') && <DuesCard />}

      <section className="grid grid-cols-2 gap-3" aria-label="Quick actions">
        <Link to="/passes?new=1" className="rounded-2xl bg-brand-600 p-4 text-white shadow-sm active:bg-brand-700">
          <UserRoundCheck className="mb-2 h-7 w-7" aria-hidden />
          <div className="font-semibold">Expecting someone?</div>
          <div className="text-sm text-white/80">Pre-approve a guest</div>
        </Link>
        <Link to="/complaints/new" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 active:bg-slate-50">
          <Ticket className="mb-2 h-7 w-7 text-brand-600" aria-hidden />
          <div className="font-semibold">Report a problem</div>
          <div className="text-sm text-slate-600">Raise a complaint</div>
        </Link>
      </section>

      <section aria-label="Recent visitors" className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Recent visitors</h2>
          <Link to="/visitors" className="-my-2 inline-flex min-h-11 items-center px-1 text-sm font-semibold text-brand-700">See all</Link>
        </div>
        {visits.isLoading ? <Spinner /> : recent.length === 0 ? <Empty title="No visitors yet" hint="Guests, deliveries and staff entering your flat will appear here." /> : recent.map((v) => <VisitRow key={v.id} v={v} to={`/visit/${v.id}`} />)}
      </section>

      {openTickets.length > 0 && (
        <section aria-label="Your open complaints" className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Your open complaints</h2>
            <Link to="/complaints" className="-my-2 inline-flex min-h-11 items-center px-1 text-sm font-semibold text-brand-700">See all</Link>
          </div>
          {openTickets.slice(0, 3).map((t) => (
            <Link key={t.id} to={`/complaints/${t.id}`}>
              <Card className="flex items-center justify-between gap-3 !p-3">
                <div className="min-w-0">
                  <div className="truncate font-semibold">#{t.number} {t.title}</div>
                  <div className="text-xs text-slate-500">{timeAgo(t.createdAt)}</div>
                </div>
                <StatusBadge map={TICKET_TONE} status={t.status} />
              </Card>
            </Link>
          ))}
        </section>
      )}
    </div>
  );
}

// ───────────── passes ─────────────

const hoursFromNow = (h: number) => new Date(Date.now() + h * 3_600_000).toISOString();

export function PassesScreen() {
  const { current } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const passes = usePasses('active');
  const revoke = useRevokePass();
  const [open, setOpen] = useState(new URLSearchParams(location.search).has('new') && current.canApproveVisitors);
  // The “?new” hint only opens the sheet once; drop it so a refresh doesn't pop the sheet up again.
  useEffect(() => {
    if (location.search) nav('/passes', { replace: true });
  }, [nav]);
  const [shown, setShown] = useState<{ code: string; name: string } | null>(null);

  const share = async (code: string, name: string) => {
    const text = `Your entry pass for ${current.communityName}, flat ${current.unitLabel}: ${code}. Show this code to the guard at the gate.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Entry pass', text });
        return;
      } catch {
        /* cancelled: fall through to copy */
      }
    }
    if (await copyText(text)) toast.success(`Pass for ${name} copied`);
    else toast.show({ title: 'Could not copy automatically', body: `Tell them the code: ${code}`, tone: 'alert' });
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Guest passes" subtitle="Share a code so your guest walks in without waiting" action={current.canApproveVisitors ? <Button size="sm" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New</Button> : undefined} />
      {passes.isLoading ? (
        <Spinner />
      ) : passes.error ? (
        <ErrorNote error={passes.error} retry={() => passes.refetch()} />
      ) : (passes.data ?? []).length === 0 ? (
        <Empty title="No active passes" hint="Create a pass for a guest, cab or the maid so the guard lets them in without calling you." action={current.canApproveVisitors ? <Button onClick={() => setOpen(true)}>Create a pass</Button> : undefined} />
      ) : (
        <div className="space-y-3">
          {passes.data!.map((p) => (
            <Card key={p.id}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-semibold">{p.visitorName}</div>
                  <div className="text-sm text-slate-600">{KIND_LABEL[p.kind]} · {p.uses}/{p.maxUses} used</div>
                  <div className="text-xs text-slate-500">Valid until {fmtDateTime(p.validUntil)}</div>
                </div>
                <div className="rounded-xl bg-brand-50 px-3 py-2 font-mono text-2xl font-bold tracking-widest text-brand-700">{p.code}</div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="secondary" className="flex-1" onClick={() => share(p.code, p.visitorName)}><Share2 className="h-4 w-4" /> Share</Button>
                <Button size="sm" variant="secondary" onClick={async () => { if (await copyText(p.code)) toast.success('Code copied'); else toast.show({ title: 'Could not copy automatically', body: `The code is ${p.code}`, tone: 'alert' }); }} aria-label="Copy code"><Copy className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="!text-rose-700" loading={revoke.isPending && revoke.variables === p.id} onClick={() => revoke.mutate(p.id, { onError: (e) => toast.error(e), onSuccess: () => toast.success('Pass cancelled') })}>Cancel</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <NewPassSheet open={open} onClose={() => { setOpen(false); nav('/passes', { replace: true }); }} onCreated={(code, name) => { setOpen(false); setShown({ code, name }); }} />
      <Sheet open={!!shown} onClose={() => setShown(null)} title="Pass ready">
        {shown && (
          <div className="space-y-4 text-center">
            <p className="text-slate-700">Give this code to <b>{shown.name}</b>. They show it to the guard at the gate.</p>
            <div className="rounded-2xl bg-brand-50 py-5 font-mono text-5xl font-bold tracking-[0.3em] text-brand-700">{shown.code}</div>
            <Button size="lg" className="w-full" onClick={() => share(shown.code, shown.name)}><Share2 className="h-5 w-5" /> Share with guest</Button>
          </div>
        )}
      </Sheet>
    </div>
  );
}

function NewPassSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (code: string, name: string) => void }) {
  const { current } = useSession();
  const create = useCreatePass();
  const toast = useToast();
  const [name, setName] = useState('');
  const [kind, setKind] = useState('guest');
  const [phone, setPhone] = useState('');
  const [hours, setHours] = useState(24);
  const [uses, setUses] = useState(1);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const p = await create.mutateAsync({ unitId: current.unitId!, visitorName: name.trim(), kind, validUntil: hoursFromNow(hours), maxUses: uses, ...(phone.trim() && { visitorPhone: phone.trim() }) });
      setName(''); setPhone('');
      onCreated(p.code, p.visitorName);
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Pre-approve a guest">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Guest name"><Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} autoFocus placeholder="e.g. Meera Aunty" /></Field>
        <Field label="Type">
          <Select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="guest">Guest</option><option value="delivery">Delivery</option><option value="cab">Cab</option><option value="service">Service (maid, cook, repair)</option>
          </Select>
        </Field>
        <Field label="Guest's phone (optional)"><Input inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98765 43210" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Valid for">
            <Select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
              <option value={6}>6 hours</option><option value={24}>1 day</option><option value={72}>3 days</option><option value={168}>1 week</option><option value={720}>30 days</option>
            </Select>
          </Field>
          <Field label="Number of visits">
            <Select value={uses} onChange={(e) => setUses(Number(e.target.value))}>
              <option value={1}>Once</option><option value={2}>Twice</option><option value={5}>5 times</option><option value={30}>30 times</option>
            </Select>
          </Field>
        </div>
        <Button type="submit" size="lg" className="w-full" loading={create.isPending} disabled={!name.trim()}>Create pass</Button>
      </form>
    </Sheet>
  );
}

// ───────────── household ─────────────

export function HouseholdScreen() {
  const { current } = useSession();
  const q = useHousehold();
  const remove = useRemoveHousehold();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const canManage = current.role === 'owner' || current.role === 'tenant';
  const mine = useMemo(() => (q.data ?? []).filter((m) => m.unitId === current.unitId), [q.data, current.unitId]);

  return (
    <div className="space-y-4">
      <PageHeader title="My household" subtitle={`Flat ${current.unitLabel}`} action={canManage ? <Button size="sm" onClick={() => setOpen(true)}><UserPlus className="h-4 w-4" /> Add</Button> : undefined} />
      {q.isLoading ? <Spinner /> : q.error ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : (
        <div className="space-y-2">
          {mine.map((m) => (
            <Card key={m.membershipId} className="flex items-center justify-between gap-3 !p-3">
              <div className="min-w-0">
                <div className="truncate font-semibold">{m.name ?? m.phone}</div>
                <div className="text-sm text-slate-600">{m.phone}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone={m.role === 'family' ? 'gray' : 'purple'}>{m.role === 'family' ? 'Family' : m.role === 'owner' ? 'Owner' : 'Tenant'}</Badge>
                {m.canApproveVisitors && <Badge tone="green">Approves visitors</Badge>}
                {canManage && m.role === 'family' && (
                  <button aria-label={`Remove ${m.name ?? m.phone}`} className="rounded-full p-2 text-rose-700 hover:bg-rose-50" onClick={() => window.confirm(`Remove ${m.name ?? m.phone} from your household? They will lose access.`) && remove.mutate(m.membershipId, { onError: (e) => toast.error(e), onSuccess: () => toast.success('Removed') })}>
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      <p className="text-sm text-slate-600">Family members get their own login for this flat. Only members marked "Approves visitors" are asked to allow or reject people at the gate.</p>
      <AddHouseholdSheet open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function AddHouseholdSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { current } = useSession();
  const add = useAddHousehold();
  const toast = useToast();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [approves, setApproves] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await add.mutateAsync({ unitId: current.unitId!, name: name.trim(), phone: phone.trim(), canApproveVisitors: approves });
      toast.success(`${name.trim()} added. They can now sign in with their number.`);
      setName(''); setPhone(''); setApproves(false); onClose();
    } catch (err) {
      toast.error(err);
    }
  }
  return (
    <Sheet open={open} onClose={onClose} title="Add a family member">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} autoFocus /></Field>
        <Field label="Mobile number" hint="They sign in with this number"><Input inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="98765 43210" /></Field>
        <label className="flex items-start gap-3 rounded-xl bg-white p-3 ring-1 ring-slate-200">
          <input type="checkbox" className="mt-1 h-5 w-5 rounded" checked={approves} onChange={(e) => setApproves(e.target.checked)} />
          <span><span className="block font-medium">Can approve visitors</span><span className="text-sm text-slate-600">They will get gate requests and can allow or reject entry.</span></span>
        </label>
        <Button type="submit" size="lg" className="w-full" loading={add.isPending} disabled={!name.trim() || !phone.trim()}>Add member</Button>
      </form>
    </Sheet>
  );
}
